import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import {
  fetchWorkers,
  fetchPriceReferences,
  updatePriceReference,
  deletePriceReference,
  createPriceReference,
} from '@/services/workers'
import type { Worker, PriceReference } from '@/types/workers'
import { API_BASE_URL } from '@/lib/axios'
import {
  AlertCircle,
  Users,
  DollarSign,
  TrendingUp,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
} from 'lucide-react'

type SortField =
  | 'name'
  | 'total_profit'
  | 'received_amount'
  | 'left_amount'
  | 'status'
  | 'created_at'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function Workers() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [workers, setWorkers] = useState<Worker[]>([])
  const [priceReferences, setPriceReferences] = useState<PriceReference[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingPrices, setIsLoadingPrices] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Price reference sheet state
  const [selectedPriceRef, setSelectedPriceRef] =
    useState<PriceReference | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [formData, setFormData] = useState({ square: '', price: '' })

  // Create price reference state
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createFormData, setCreateFormData] = useState({
    square: '',
    price: '',
  })

  useEffect(() => {
    const loadWorkers = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchWorkers()
        setWorkers(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workers')
      } finally {
        setIsLoading(false)
      }
    }

    const loadPriceReferences = async () => {
      try {
        setIsLoadingPrices(true)
        const data = await fetchPriceReferences()
        setPriceReferences(data)
      } catch (err) {
        console.error('Failed to load price references:', err)
      } finally {
        setIsLoadingPrices(false)
      }
    }

    loadWorkers()
    loadPriceReferences()
  }, [])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className='ml-1 h-3 w-3' />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className='ml-1 h-3 w-3' />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className='ml-1 h-3 w-3' />
    }
    return <ArrowUpDown className='ml-1 h-3 w-3' />
  }

  const filteredWorkers = useMemo(() => {
    let result = workers.filter((worker) => {
      const searchLower = searchQuery.toLowerCase()
      const fullName =
        `${worker.account.first_name} ${worker.account.last_name}`.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        fullName.includes(searchLower) ||
        worker.account.email.toLowerCase().includes(searchLower)

      const matchesStatus =
        filterStatus === 'all' || worker.worker_status === filterStatus

      return matchesSearch && matchesStatus
    })

    // Always sort pending workers to the top first
    result = [...result].sort((a, b) => {
      const aIsPending = a.worker_status === 'pending' ? 0 : 1
      const bIsPending = b.worker_status === 'pending' ? 0 : 1
      if (aIsPending !== bIsPending) {
        return aIsPending - bIsPending
      }

      // Then apply user-selected sorting
      if (sortField && sortDirection) {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'name':
            aValue = `${a.account.first_name} ${a.account.last_name}`
            bValue = `${b.account.first_name} ${b.account.last_name}`
            break
          case 'total_profit':
            aValue = parseFloat(a.total_profit)
            bValue = parseFloat(b.total_profit)
            break
          case 'received_amount':
            aValue = parseFloat(a.received_amount)
            bValue = parseFloat(b.received_amount)
            break
          case 'left_amount':
            aValue = parseFloat(a.left_amount)
            bValue = parseFloat(b.left_amount)
            break
          case 'status':
            aValue = a.worker_status
            bValue = b.worker_status
            break
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      }

      return 0
    })

    return result
  }, [workers, searchQuery, filterStatus, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
    setFilterStatus('all')
  }

  const hasActiveFilters = searchQuery !== '' || filterStatus !== 'all'

  const handlePriceRefClick = (priceRef: PriceReference) => {
    setSelectedPriceRef(priceRef)
    setFormData({
      square: priceRef.square,
      price: priceRef.price.toString(),
    })
    setIsSheetOpen(true)
  }

  const handleUpdatePriceRef = async () => {
    if (!selectedPriceRef) return

    try {
      setIsUpdating(true)
      const updated = await updatePriceReference(selectedPriceRef.id, {
        square: formData.square,
        price: parseFloat(formData.price),
      })
      setPriceReferences((prev) =>
        prev.map((ref) => (ref.id === updated.id ? updated : ref))
      )
      setIsSheetOpen(false)
    } catch (err) {
      console.error('Failed to update price reference:', err)
      alert('Failed to update price reference')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeletePriceRef = async () => {
    if (!selectedPriceRef) return

    try {
      setIsDeleting(true)
      await deletePriceReference(selectedPriceRef.id)
      setPriceReferences((prev) =>
        prev.filter((ref) => ref.id !== selectedPriceRef.id)
      )
      setIsDeleteDialogOpen(false)
      setIsSheetOpen(false)
    } catch (err) {
      console.error('Failed to delete price reference:', err)
      alert('Failed to delete price reference')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCreatePriceRef = async () => {
    if (!createFormData.square.trim() || !createFormData.price) return

    try {
      setIsCreating(true)
      const created = await createPriceReference({
        square: createFormData.square.trim(),
        price: parseFloat(createFormData.price),
      })
      setPriceReferences((prev) => [...prev, created])
      setCreateFormData({ square: '', price: '' })
      setIsCreateSheetOpen(false)
    } catch (err) {
      console.error('Failed to create price reference:', err)
      alert('Failed to create price reference')
    } finally {
      setIsCreating(false)
    }
  }

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(num)
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 10,
    }).format(price)
  }

  const getAvatarUrl = (avatar: string | null) => {
    if (!avatar) return ''
    if (avatar.startsWith('http')) return avatar
    const baseUrl = API_BASE_URL.replace(/\/api$/, '')
    return `${baseUrl}${avatar}`
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
      case 'inactive':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  const totalWorkers = workers.length
  const totalProfit = workers.reduce(
    (sum, w) => sum + parseFloat(w.total_profit),
    0
  )
  const totalReceived = workers.reduce(
    (sum, w) => sum + parseFloat(w.received_amount),
    0
  )

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('workers.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-3'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('workers.totalWorkers')}
              </CardTitle>
              <Users className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalWorkers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('workers.totalProfit')}
              </CardTitle>
              <TrendingUp className='h-4 w-4 text-green-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                {formatCurrency(totalProfit)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('workers.totalPaid')}
              </CardTitle>
              <DollarSign className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {formatCurrency(totalReceived)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Price References Table */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Price References</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingPrices ? (
              <Skeleton className='h-16 w-full' />
            ) : priceReferences.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                No price references found.
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <Table className='w-auto'>
                  <TableBody>
                    <TableRow>
                      <TableCell className='font-semibold text-lg text-center border-r-2'>Square</TableCell>
                      {priceReferences.map((ref) => (
                        <TableCell
                          key={ref.id}
                          className='text-center border-r-2 whitespace-nowrap'
                        >
                          {ref.square}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className='font-semibold text-lg text-center border-r-2'>Price</TableCell>
                      {priceReferences.map((ref) => (
                        <TableCell
                          key={ref.id}
                          className='text-center border-r-2 whitespace-nowrap'
                        >
                          {formatCurrency(ref.price)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card> */}

        {/* Tables Grid - Price References and Workers */}
        <div className='grid gap-4 lg:grid-cols-[200px_1fr]'>
          {/* Price References Table */}
          <Card>
            <CardHeader className='px-3'>
              <div className='flex items-center justify-between'>
                <CardTitle>{t('workers.priceReferences')}</CardTitle>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => setIsCreateSheetOpen(true)}
                >
                  <Plus className='h-4 w-4' />
                </Button>
              </div>
            </CardHeader>
            <CardContent className='px-3'>
              {isLoadingPrices ? (
                <LoadingSkeleton />
              ) : priceReferences.length === 0 ? (
                <div className='py-8 text-center text-muted-foreground'>
                  {t('workers.noPriceReferences')}
                </div>
              ) : (
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('workers.square')}</TableHead>
                        <TableHead className='text-right'>
                          {t('common.price')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceReferences.map((ref) => (
                        <TableRow
                          key={ref.id}
                          className='cursor-pointer'
                          onClick={() => handlePriceRefClick(ref)}
                        >
                          <TableCell className='font-medium'>
                            {ref.square}
                          </TableCell>
                          <TableCell className='text-right'>
                            {formatPrice(ref.price)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Workers Table */}
          <Card>
            <CardHeader>
              <CardTitle>{t('workers.title')}</CardTitle>
              <CardDescription>{t('workers.manageWorkers')}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className='mb-4 flex flex-wrap items-center gap-3'>
                <div className='relative flex-1 min-w-[200px]'>
                  <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder={t('workers.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className='pl-9'
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className='w-[140px]'>
                    <SelectValue placeholder={t('common.status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>
                      {t('workers.allStatus')}
                    </SelectItem>
                    <SelectItem value='accepted'>
                      {t('workers.accepted')}
                    </SelectItem>
                    <SelectItem value='pending'>
                      {t('workers.pending')}
                    </SelectItem>
                    <SelectItem value='rejected'>
                      {t('workers.rejected')}
                    </SelectItem>
                    <SelectItem value='inactive'>
                      {t('workers.inactive')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {hasActiveFilters && (
                  <Button variant='ghost' size='sm' onClick={clearFilters}>
                    <X className='mr-1 h-4 w-4' />
                    {t('common.clear')}
                  </Button>
                )}
              </div>

              {isLoading ? (
                <LoadingSkeleton />
              ) : filteredWorkers.length === 0 ? (
                <div className='py-8 text-center text-muted-foreground'>
                  {workers.length === 0
                    ? t('workers.noWorkersFound')
                    : t('workers.noWorkersMatch')}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('name')}
                      >
                        <div className='flex items-center'>
                          {t('workers.worker')}
                          {getSortIcon('name')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('status')}
                      >
                        <div className='flex items-center'>
                          {t('common.status')}
                          {getSortIcon('status')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('total_profit')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('workers.totalProfit')}
                          {getSortIcon('total_profit')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('received_amount')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('workers.received')}
                          {getSortIcon('received_amount')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50 text-right'
                        onClick={() => handleSort('left_amount')}
                      >
                        <div className='flex items-center justify-end'>
                          {t('workers.balance')}
                          {getSortIcon('left_amount')}
                        </div>
                      </TableHead>
                      <TableHead
                        className='cursor-pointer select-none hover:bg-muted/50'
                        onClick={() => handleSort('created_at')}
                      >
                        <div className='flex items-center'>
                          {t('workers.joined')}
                          {getSortIcon('created_at')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWorkers.map((worker) => (
                      <TableRow
                        key={worker.id}
                        className={`cursor-pointer ${
                          worker.worker_status === 'pending'
                            ? 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-950/30 dark:hover:bg-yellow-950/50'
                            : ''
                        }`}
                        onClick={() => navigate(`/workers/${worker.id}`)}
                      >
                        <TableCell>
                          <div className='flex items-center gap-3'>
                            <Avatar className='h-9 w-9'>
                              <AvatarImage
                                src={getAvatarUrl(worker.account.avatar)}
                                alt={`${worker.account.first_name} ${worker.account.last_name}`}
                              />
                              <AvatarFallback>
                                {getInitials(
                                  worker.account.first_name,
                                  worker.account.last_name
                                )}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className='font-medium'>
                                {worker.account.first_name}{' '}
                                {worker.account.last_name}
                              </div>
                              <div className='text-sm text-muted-foreground'>
                                {worker.account.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {worker.worker_status === 'pending' ? (
                            <div className='flex items-center gap-2'>
                              <span className='relative flex h-2 w-2'>
                                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75'></span>
                                <span className='relative inline-flex h-2 w-2 rounded-full bg-yellow-500'></span>
                              </span>
                              <span className='rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'>
                                {t('workers.pendingApproval')}
                              </span>
                            </div>
                          ) : (
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(
                                worker.worker_status
                              )}`}
                            >
                              {t(`status.${worker.worker_status}`)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className='text-right font-medium text-green-600 dark:text-green-400'>
                          {formatCurrency(worker.total_profit)}
                        </TableCell>
                        <TableCell className='text-right'>
                          {formatCurrency(worker.received_amount)}
                        </TableCell>
                        <TableCell className='text-right'>
                          <span
                            className={
                              parseFloat(worker.left_amount) > 0
                                ? 'text-amber-600 dark:text-amber-400'
                                : parseFloat(worker.left_amount) < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-muted-foreground'
                            }
                          >
                            {formatCurrency(worker.left_amount)}
                          </span>
                        </TableCell>
                        <TableCell className='text-muted-foreground'>
                          {formatDate(worker.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Results count */}
              {!isLoading && workers.length > 0 && (
                <div className='mt-4 text-sm text-muted-foreground'>
                  {t('common.showing')} {filteredWorkers.length}{' '}
                  {t('common.of')} {workers.length}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Price Reference Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className='px-4 overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>{t('workers.priceRefDetails')}</SheetTitle>
            <SheetDescription>{t('workers.updateOrDelete')}</SheetDescription>
          </SheetHeader>
          <div className='mt-2 space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='square'>{t('workers.square')}</Label>
              <Input
                id='square'
                value={formData.square}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, square: e.target.value }))
                }
                placeholder={t('workers.enterSquare')}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='price'>{t('common.price')}</Label>
              <Input
                id='price'
                type='number'
                step='0.01'
                value={formData.price}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, price: e.target.value }))
                }
                placeholder={t('workers.enterPrice')}
              />
            </div>

            <div className='flex flex-col gap-2 pt-4'>
              <Button
                onClick={handleUpdatePriceRef}
                disabled={isUpdating}
                className='w-full'
              >
                {isUpdating
                  ? t('workers.updating')
                  : t('workers.updatePriceRef')}
              </Button>

              <Button
                variant='destructive'
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={isDeleting}
                className='w-full'
              >
                <Trash2 className='mr-2 h-4 w-4' />
                {t('workers.deletePriceRef')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Create Price Reference Sheet */}
      <Sheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen}>
        <SheetContent className='px-4 overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>{t('workers.createPriceRef')}</SheetTitle>
            <SheetDescription>
              {t('workers.createPriceRefDesc')}
            </SheetDescription>
          </SheetHeader>
          <div className='mt-2 space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='create-square'>{t('workers.square')}</Label>
              <Input
                id='create-square'
                value={createFormData.square}
                onChange={(e) =>
                  setCreateFormData((prev) => ({
                    ...prev,
                    square: e.target.value,
                  }))
                }
                placeholder={t('workers.enterSquare')}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='create-price'>{t('common.price')}</Label>
              <Input
                id='create-price'
                type='number'
                step='0.01'
                value={createFormData.price}
                onChange={(e) =>
                  setCreateFormData((prev) => ({
                    ...prev,
                    price: e.target.value,
                  }))
                }
                placeholder={t('workers.enterPrice')}
              />
            </div>

            <div className='pt-4'>
              <Button
                onClick={handleCreatePriceRef}
                disabled={
                  isCreating ||
                  !createFormData.square.trim() ||
                  !createFormData.price
                }
                className='w-full'
              >
                {isCreating ? t('common.loading') : t('common.create')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workers.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workers.deleteConfirmation', {
                square: selectedPriceRef?.square,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePriceRef}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90'
            >
              {isDeleting ? t('workers.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
